import type { FieldCovariate } from "../types/contract";
import { covariateValueExtent } from "./stackClient";

export const NUTRIENT_STANDARDS = [
  { id: "p", std: 15, re: /(^|_)p(_|$)/i },
  { id: "k", std: 110, re: /(^|_)k(_|$)/i },
  { id: "ph", std: 6.3, re: /ph/i },
  { id: "om", std: 3, re: /om/i },
  { id: "cec", std: 15, re: /cec/i },
] as const;

export type NutrientId = (typeof NUTRIENT_STANDARDS)[number]["id"];

/*
 *
 * MIN(max − standard, standard − min) / standard.
 * A layer that sits entirely above or below the standard scores negative on the missing side.
 */
export function minRangeRelativeToStandard(
  min: number,
  max: number,
  std: number,
): number | null {
  if (
    !Number.isFinite(min) ||
    !Number.isFinite(max) ||
    !(max >= min) ||
    !(std > 0)
  )
    return null;
  return Math.min(max - std, std - min) / std;
}

function uncorrectedModel(c: FieldCovariate): boolean {
  if (c.has_values === false || c.category !== "model") return false;
  // Live keys are `model__uncorrected_*`. The mock catalog uses `model__p_ppm` / `model__om_pct`.
  return (
    /uncorrected/i.test(c.key) ||
    /^model__(om_pct|om|ph|p_ppm|p|k_ppm|k|cec)$/i.test(c.key)
  );
}

/* * The five uncorrected models on this field, in P, K, pH, OM, CEC order. */
export function uncorrectedNutrientCovs(
  covs: FieldCovariate[],
): { id: NutrientId; std: number; cov: FieldCovariate }[] {
  const models = covs.filter(uncorrectedModel);
  const out: { id: NutrientId; std: number; cov: FieldCovariate }[] = [];
  for (const spec of NUTRIENT_STANDARDS) {
    const cov = models.find((c) => spec.re.test(c.key));
    if (cov) out.push({ id: spec.id, std: spec.std, cov });
  }
  return out;
}

/* * Highest minimum-range score. An exact tie keeps the earlier nutrient (P, then K, pH, OM, CEC). */
export function pickWidestNutrient(
  rows: { id: NutrientId; min: number; max: number }[],
): NutrientId | null {
  let bestId: NutrientId | null = null;
  let best = -Infinity;
  for (const spec of NUTRIENT_STANDARDS) {
    const row = rows.find((r) => r.id === spec.id);
    if (!row) continue;
    const score = minRangeRelativeToStandard(row.min, row.max, spec.std);
    if (score == null || !(score > best)) continue;
    best = score;
    bestId = spec.id;
  }
  return bestId;
}

/* * Read each uncorrected model's own cell min/max and return the winning covariate. */
export async function pickWidestUncorrectedModel(
  fieldId: string,
  covs: FieldCovariate[],
): Promise<FieldCovariate | null> {
  const candidates = uncorrectedNutrientCovs(covs);
  const scored = await Promise.all(
    candidates.map(async (c) => {
      const ext = await covariateValueExtent(fieldId, c.cov).catch(() => null);
      return ext ? { id: c.id, min: ext.min, max: ext.max, cov: c.cov } : null;
    }),
  );
  const id = pickWidestNutrient(
    scored.filter((r): r is NonNullable<typeof r> => r != null),
  );
  return scored.find((r) => r?.id === id)?.cov ?? null;
}
